import Modal from '../shared/Modal.jsx';
import { centsToUsd } from '../../lib/format.js';

// Read-only detail view for a clock entry. Shows the standard clock-in/out
// info plus the pay breakdown (rate that applied, regular vs OT, segments if
// the shift spanned a rate change).
//
// Admins get an "Edit" button to open EntryEditModal; crew see read-only.
export default function EntryDetailModal({ entry, onClose, onEdit, isAdmin }) {
  const start = new Date(entry.clock_in_at);
  const end = entry.clock_out_at ? new Date(entry.clock_out_at) : null;
  const isOpen = !end;
  const durationMs = (end || new Date()) - start;
  const durHrs = durationMs / 3_600_000;
  const pay = entry.pay_detail;

  return (
    <Modal onClose={onClose} title={`Shift · ${start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}`} icon="ph-clock">
      <Section label="When">
        <Row k="Started"   v={start.toLocaleString()} />
        <Row k="Ended"     v={end ? end.toLocaleString() : <span className="badge orange">still open</span>} />
        <Row k="Duration"  v={durHrs.toFixed(2) + ' hrs'} />
      </Section>

      <Section label="Job">
        <Row k="Site"      v={entry.job_name || <em style={{ color: 'var(--text-muted)' }}>— none —</em>} />
        {entry.clock_in_lat != null && (
          <Row k="Clock-in" v={`${entry.clock_in_lat.toFixed(5)}, ${entry.clock_in_lng.toFixed(5)}`} />
        )}
        {entry.clock_out_lat != null && (
          <Row k="Clock-out" v={`${entry.clock_out_lat.toFixed(5)}, ${entry.clock_out_lng.toFixed(5)}`} />
        )}
        <Row k="Source"    v={<span className="badge">{entry.source_in}{entry.source_out ? ' → ' + entry.source_out : ''}</span>} />
      </Section>

      {entry.notes && (
        <Section label="Notes">
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: 'var(--on-surface-variant)' }}>
            {entry.notes}
          </div>
        </Section>
      )}

      {pay && !isOpen && (
        <Section label="Pay for this shift">
          {pay.segments.length === 1 ? (
            <SegmentSimple seg={pay.segments[0]} />
          ) : (
            <>
              <div style={{ fontSize: 11, color: 'var(--warm-gray)', textTransform: 'uppercase', letterSpacing: '0.06rem', marginBottom: 8 }}>
                Shift spanned {pay.segments.length} rate periods
              </div>
              {pay.segments.map((s, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <SegmentSimple seg={s} />
                </div>
              ))}
            </>
          )}
          <hr style={{ border: 0, borderTop: '1px solid var(--surface-container-high)', margin: '12px 0' }} />
          <Row k="Regular pay" v={centsToUsd(pay.regular_pay_cents) + (pay.regular_hrs ? ` (${pay.regular_hrs.toFixed(2)} hrs)` : '')} />
          {pay.ot_hrs > 0 && (
            <Row k="OT pay" v={centsToUsd(pay.ot_pay_cents) + ` (${pay.ot_hrs.toFixed(2)} hrs)`} accent />
          )}
          <Row k="Total" v={<strong style={{ color: 'var(--orange)' }}>{centsToUsd(pay.total_pay_cents)}</strong>} />
          {pay.cumulative_hrs_before > 0 && pay.employment_type === 'w2' && (
            <div style={{ fontSize: 11, color: 'var(--warm-gray)', marginTop: 8 }}>
              {pay.cumulative_hrs_before.toFixed(2)} hrs already worked this period before this shift.
            </div>
          )}
        </Section>
      )}
      {isOpen && (
        <Section label="Pay">
          <div style={{ color: 'var(--warm-gray)', fontSize: 13 }}>
            Pay finalizes after clock-out.
          </div>
        </Section>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        {isAdmin && onEdit && (
          <button className="btn-sm primary" onClick={onEdit}>
            <i className="ph ph-pencil-simple" /> Edit
          </button>
        )}
        <button className="btn-sm" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontFamily: 'var(--font-label)', fontSize: 10, fontWeight: 700,
        letterSpacing: '0.1rem', textTransform: 'uppercase',
        color: 'var(--orange)', marginBottom: 8,
      }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

function Row({ k, v, accent }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '6px 0',
      fontSize: 13,
      color: accent ? 'var(--orange)' : 'var(--on-surface)',
    }}>
      <span style={{
        fontFamily: 'var(--font-label)', fontSize: 11,
        textTransform: 'uppercase', letterSpacing: '0.06rem',
        color: 'var(--warm-gray)',
      }}>{k}</span>
      <span style={{ textAlign: 'right' }}>{v}</span>
    </div>
  );
}

function SegmentSimple({ seg }) {
  return (
    <div style={{ fontSize: 13 }}>
      <div>{(seg.regular_hrs + seg.ot_hrs).toFixed(2)} hrs at {centsToUsd(seg.hourly_rate_cents)}/h</div>
      {seg.ot_hrs > 0 && (
        <div style={{ color: 'var(--orange)', fontSize: 12 }}>
          ({seg.regular_hrs.toFixed(2)} reg + {seg.ot_hrs.toFixed(2)} OT at {seg.ot_multiplier}×)
        </div>
      )}
    </div>
  );
}
